import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Avatar,
    Chip,
    useTheme,
    Stack,
    Fade,
    Zoom,
} from '@mui/material';

import {
    Devices as DevicesIcon,
    BugReport as BugIcon,
    Speed as SpeedIcon,
    Security as SecurityIcon,
    CheckCircle as CheckCircleIcon,
    PhoneAndroid as MobileIcon,
    Tablet as TabletIcon,
    Computer as DesktopIcon,
    Star as StarIcon,
    Analytics as AnalyticsIcon,
    Code as CodeIcon,
    CompareArrows as CompareArrowsIcon,
    Accessibility as AccessibilityIcon,
    Api as ApiIcon,
} from '@mui/icons-material';

import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';
import { alpha } from '@mui/system';

// Professional color palette (Same as WebsiteDevelopment)
const colors = {
    primary: '#4551be', // Exact blue
    secondary: '#6b77d4', // Lighter blue accent
    success: '#10b981', // Green
    warning: '#f59e0b', // Orange
    dark: '#1e293b', // Dark text
    light: '#f0f4f8', // Light background
    gray: '#64748b', // Slate 500
    accent: '#4551be',
    textSecondary: '#64748b', // Dark grey for secondary text
    gradient: 'linear-gradient(135deg, #4551be 0%, #6b77d4 100%)'
};

// Animation keyframes
const float = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
`;

const ManualTestingServices = () => {
    const theme = useTheme();

    const services = [
        {
            id: 1,
            title: 'Responsive Web Testing',
            description: 'Comprehensive testing across all devices and screen sizes. Ensure your website looks perfect on mobile, tablet and desktop.',
            icon: <DevicesIcon />,
            color: colors.primary,
            features: ['320px to 4K displays', 'Orientation testing', 'Touch optimization', 'Cross-browser testing']
        },
        {
            id: 2,
            title: 'Bug Detection & Reporting',
            description: 'Detailed bug reports with screenshots, steps to reproduce and severity analysis. Catch issues before your users do.',
            icon: <BugIcon />,
            color: colors.warning,
            features: ['Visual bug tracking', 'Priority classification', 'Regression testing', 'Bug verification']
        },
        {
            id: 3,
            title: 'Performance Testing',
            description: 'Load time analysis, stress testing and optimization recommendations for lightning-fast user experiences.',
            icon: <SpeedIcon />,
            color: colors.success,
            features: ['3G/4G/5G networks', 'Load time metrics', 'Stress testing', 'Optimization tips']
        },
        {
            id: 4,
            title: 'Security Testing',
            description: 'Identify vulnerabilities, test authentication systems and ensure data protection across your applications.',
            icon: <SecurityIcon />,
            color: colors.secondary,
            features: ['Authentication testing', 'Data encryption', 'Session management', 'Vulnerability scanning']
        },
        {
            id: 5,
            title: 'Usability Testing',
            description: 'Evaluate user experience, navigation flow and interface design to ensure intuitive and engaging interactions.',
            icon: <AccessibilityIcon />,
            color: colors.success,
            features: ['User flow analysis', 'Navigation testing', 'Interface evaluation', 'Accessibility checks']
        },
        {
            id: 6,
            title: 'Compatibility Testing',
            description: 'Test across different browsers, operating systems and devices to ensure consistent experience everywhere.',
            icon: <CompareArrowsIcon />,
            color: colors.primary,
            features: ['Cross-browser testing', 'OS compatibility', 'Device testing', 'Version testing']
        },
    ];

    const stats = [
        { value: '10,000+', label: 'Tests Completed', icon: <CheckCircleIcon />, color: '#10b981' },
        { value: '500+', label: 'Bugs Found', icon: <BugIcon />, color: '#f59e0b' },
        { value: '50+', label: 'Devices Tested', icon: <DevicesIcon />, color: '#4551be' },
        { value: '98%', label: 'Client Satisfaction', icon: <StarIcon />, color: '#8b5cf6' }
    ];

    const process = [
        { step: "01", title: "Requirement Analysis", description: "We analyze your testing requirements and create a comprehensive test strategy" },
        { step: "02", title: "Test Planning", description: "We develop detailed test plans, test cases and testing schedules" },
        { step: "03", title: "Test Execution", description: "We execute test cases and document results systematically" },
        { step: "04", title: "Defect Tracking", description: "We track and manage defects through our robust bug tracking system" },
        { step: "05", title: "Reporting", description: "We provide detailed test reports and quality metrics" },
        { step: "06", title: "Test Closure", description: "We ensure all test objectives are met and deliver final test summary" }
    ];

    const benefits = [
        {
            icon: <StarIcon />,
            title: "Expertise & Experience",
            description: "Our team has 10+ years of software testing experience with proven methodologies and best practices."
        },
        {
            icon: <SecurityIcon />,
            title: "Quality Assurance",
            description: "Comprehensive quality assurance services that guarantee bug-free, high-quality software delivery."
        },
        {
            icon: <AnalyticsIcon />,
            title: "Cost Efficiency",
            description: "Early bug detection reduces overall development costs by up to 40%."
        },
        {
            icon: <DevicesIcon />,
            title: "Comprehensive Coverage",
            description: "End-to-end testing coverage across all stages of software development lifecycle."
        }
    ];

    const devices = [
        { name: 'iPhone 15 Pro', icon: <MobileIcon />, count: '2,500+ tests' },
        { name: 'Samsung S24', icon: <MobileIcon />, count: '2,100+ tests' },
        { name: 'iPad Pro', icon: <TabletIcon />, count: '1,800+ tests' },
        { name: 'MacBook Pro', icon: <DesktopIcon />, count: '3,200+ tests' },
        { name: 'Pixel 8', icon: <MobileIcon />, count: '1,900+ tests' },
        { name: 'Surface Pro', icon: <TabletIcon />, count: '1,500+ tests' },
        { name: 'iPhone SE', icon: <MobileIcon />, count: '1,200+ tests' },
        { name: 'Galaxy Tab S9', icon: <TabletIcon />, count: '1,100+ tests' }
    ];

    return (
        <Box sx={{ bgcolor: colors.light, minHeight: '100vh' }}>
            <style>{float}</style>
            <Navbar />

            {/* Hero Section */}
                  <Box
              sx={{
                minHeight: { xs: 'auto', md: '60vh' },
                display: 'flex',
                alignItems: 'center',
            
                // Navbar overlap fix
               pt: { xs: 9, sm: 7, md: 8 },
                pb: { xs: 7, sm: 9, md: 12 },
            
                background: `linear-gradient(
                  135deg,
                  ${alpha(colors.primary, 0.03)} 0%,
                  ${alpha(colors.secondary, 0.03)} 100%
                )`,
            
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
              }}
            >
                <Container maxWidth="xl">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Fade in timeout={1000}>
                                <Box>
                                    <Chip
                                        icon={<BugIcon />}
                                        label="⚡ MANUAL TESTING EXPERTS"
                                        sx={{
                                            mb: 3,
                                            mt: { xs: 2, md: 10 },
                                            bgcolor: alpha(colors.primary, 0.1),
                                            color: colors.primary,
                                            fontWeight: 600,
                                            border: `1px solid ${alpha(colors.primary, 0.2)}`
                                        }}
                                    />

                                    <Typography variant="h1" sx={{
                                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                                        fontWeight: 800,
                                        lineHeight: 1.2,
                                        mb: 3,
                                        color: colors.dark
                                    }}>
                                        Manual Testing
                                        <Box component="span" sx={{
                                            color: colors.primary,
                                            display: 'block',
                                            background: colors.gradient,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}>
                                            Excellence
                                        </Box>
                                    </Typography>

                                    <Typography sx={{
                                        color: colors.textSecondary,
                                        mb: 4,
                                        fontSize: '1.2rem',
                                        lineHeight: 1.7,
                                        maxWidth: '90%'
                                    }}>
                                        We provide comprehensive manual testing services to ensure your applications are bug-free,
                                        secure and perform optimally. Our testing solutions combine human expertise with advanced
                                        technology to deliver quality results.
                                    </Typography>
                                </Box>
                            </Fade>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                                <Box sx={{
                                    animation: `float 6s ease-in-out infinite`,
                                    textAlign: 'center',
                                    position: 'relative',
                                    width: '100%',
                                    maxWidth: 500,
                                    mx: 'auto',
                                    py: { xs: 4, md: 2 },
                                }}>
                                    {/* Main blob */}
                                    <Box sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: { xs: '240px', sm: '300px', md: '360px' },
                                        height: { xs: '240px', sm: '300px', md: '360px' },
                                        borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                                        background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, ${alpha(colors.secondary, 0.1)} 100%)`,
                                        position: 'relative',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -15,
                                            left: -15,
                                            right: -15,
                                            bottom: -15,
                                            borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                                            background: `linear-gradient(135deg, ${alpha(colors.primary, 0.2)} 0%, ${alpha(colors.secondary, 0.2)} 100%)`,
                                            zIndex: -1,
                                            animation: `float 8s ease-in-out infinite reverse`
                                        }
                                    }}>
                                        <BugIcon sx={{
                                            fontSize: { xs: 80, sm: 120, md: 150 },
                                            color: colors.primary
                                        }} />
                                    </Box>
                                </Box>
                            </Zoom>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Stats Section */}
            <Container maxWidth="lg" sx={{ mt: -5, mb: 8, position: 'relative', zIndex: 2 }}>
                <Paper elevation={4} sx={{ borderRadius: 4, p: 4 }}>
                    <Grid container spacing={3}>
                        {stats.map((stat, index) => (
                            <Grid item xs={6} md={3} key={index}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Avatar sx={{
                                        bgcolor: stat.color,
                                        width: 60,
                                        height: 60,
                                        mx: 'auto',
                                        mb: 2
                                    }}>
                                        {stat.icon}
                                    </Avatar>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: colors.dark }}>
                                        {stat.value}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                        {stat.label}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Container>

            {/* Process Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Box textAlign="center" mb={6}>
                    <Chip
                        label="OUR TESTING PROCESS"
                        sx={{
                            bgcolor: alpha(colors.primary, 0.1),
                            color: colors.primary,
                            fontWeight: 600,
                            mb: 2
                        }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
                        How We Test Your Software
                    </Typography>
                    <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
                        A systematic approach to ensure comprehensive test coverage
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {process.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper sx={{
                                p: 3,
                                height: '100%',
                                position: 'relative',
                                transition: 'transform 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                                }
                            }}>
                                <Typography variant="h2" sx={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 20,
                                    fontSize: '4rem',
                                    fontWeight: 900,
                                    color: alpha(colors.primary, 0.1),
                                    lineHeight: 1
                                }}>
                                    {item.step}
                                </Typography>
                                <Box sx={{ position: 'relative', zIndex: 1 }}>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: colors.primary }}>
                                        {item.title}
                                    </Typography>
                                    <Typography sx={{ color: colors.textSecondary }}>
                                        {item.description}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Services Section */}
            <Box sx={{ bgcolor: 'white', py: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Chip
                            label="OUR TESTING SERVICES"
                            sx={{
                                bgcolor: alpha(colors.primary, 0.1),
                                color: colors.primary,
                                fontWeight: 600,
                                mb: 2
                            }}
                        />
                        <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
                            Comprehensive Testing Solutions
                        </Typography>
                        <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
                            We provide end-to-end testing services to ensure your software meets the highest quality standards
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {services.map((service, index) => (
                            <Grid item xs={12} md={6} lg={4} key={service.id}>
                                <Card sx={{
                                    height: '100%',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: `0 20px 40px -12px ${alpha(colors.primary, 0.2)}`
                                    }
                                }}>
                                    <CardContent sx={{ p: 4 }}>
                                        <Box sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: 2,
                                            background: alpha(service.color, 0.1),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                            color: service.color
                                        }}>
                                            {service.icon}
                                        </Box>

                                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: colors.dark }}>
                                            {service.title}
                                        </Typography>

                                        <Typography paragraph sx={{ color: colors.textSecondary }}>
                                            {service.description}
                                        </Typography>

                                        <Stack spacing={1} sx={{ mt: 2 }}>
                                            {service.features.map((feature, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <CheckCircleIcon sx={{ fontSize: 16, color: colors.success }} />
                                                    <Typography variant="body2" sx={{ color: colors.dark }}>{feature}</Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Devices Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Box textAlign="center" mb={6}>
                    <Chip
                        label="TESTED DEVICES"
                        sx={{
                            bgcolor: alpha(colors.primary, 0.1),
                            color: colors.primary,
                            fontWeight: 600,
                            mb: 2
                        }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
                        Real Devices We Test On
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {devices.map((device, index) => (
                        <Grid item xs={6} md={3} key={index}>
                            <Paper sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                transition: 'transform 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                                }
                            }}>
                                <Avatar sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary, width: 50, height: 50, mx: 'auto', mb: 2 }}>
                                    {device.icon}
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: colors.dark }}>
                                    {device.name}
                                </Typography>
                                <Chip label={device.count} size="small" sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary }} />
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Why Choose Us */}
            <Box sx={{ bgcolor: 'white', py: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Chip
                            label="WHY CHOOSE US"
                            sx={{
                                bgcolor: alpha(colors.primary, 0.1),
                                color: colors.primary,
                                fontWeight: 600,
                                mb: 2
                            }}
                        />
                        <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
                            Why Leading Companies Trust Us
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {benefits.map((benefit, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Paper sx={{
                                    p: 3,
                                    height: '100%',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                                    }
                                }}>
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        background: colors.gradient,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2,
                                        color: 'white'
                                    }}>
                                        {benefit.icon}
                                    </Box>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: colors.dark }}>
                                        {benefit.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                        {benefit.description}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box sx={{ background: colors.gradient, py: 8 }}>
                <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#ffffff' }}>
                        Ready to Ensure Software Quality?
                    </Typography>
                    <Typography variant="h6" sx={{ color: alpha('#ffffff', 0.9), mb: 4 }}>
                        Join 500+ companies that trust us with their software testing needs.
                    </Typography>
                </Container>
            </Box>

            <HomeFooter />
        </Box>
    );
};

export default ManualTestingServices;